// components/data-quality/DataQualityDashboard.jsx
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faSpinner,
  faFilter,
  faCalendarAlt,
  faTimes,
  faChevronDown,
  faSearch,
  faSync,
  faInfoCircle,
  faBuilding,
  faMapMarkerAlt,
  faExclamationTriangle,
  faCheckCircle,
  faDownload,
  faIdCard,
} from "@fortawesome/free-solid-svg-icons";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Select from "react-select";
import axios from "axios";

// Import components
import OverallQualityMetrics from "./OverallQualityMetrics";
import QualityDistributionChart from "./QualityDistributionChart";
import QualityTrendChart from "./QualityTrendChart";
import StatusProgressionChart from "./StatusProgressionChart";
import DateQualityAnalysisChart from "./DateQualityAnalysisChart";
import AbandonedBookingsChart from "./AbandonedBookingsChart";
import TerminalQualityComparison from "./TerminalQualityComparison";
import CustomerQualityComparison from "./CustomerQualityComparison";
import BookingQualityDetailsModal from "./BookingQualityDetailsModal";
import AduanaQualityComparison from "./AduanaQualityComparison";
import DataQualityIssuesMatrix from "./DataQualityIssuesMatrix";

const DataQualityDashboard = () => {
  // State for data
  const [dataQuality, setDataQuality] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for filters
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // Filter states
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedTerminals, setSelectedTerminals] = useState([]);
  const [qualityRange, setQualityRange] = useState([0, 100]);
  const [searchTerm, setSearchTerm] = useState("");
  const [abandonedFilter, setAbandonedFilter] = useState("all"); // 'all', 'abandoned', 'active'

  // Filter options
  const [customerOptions, setCustomerOptions] = useState([]);
  const [terminalOptions, setTerminalOptions] = useState([]);

  // Selected booking for detailed view
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);

  // Pagination state for bookings table
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
  });

  // Chart layout and UI control
  const [chartLayout, setChartLayout] = useState({
    columns: 2, // Default to 2 columns
    size: "medium", // Options: small, medium, large
  });

  // Fetch data quality analytics
  const fetchDataQualityAnalytics = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();

      // Add date range if set
      if (dateRange[0].startDate && dateRange[0].endDate) {
        params.append("startDate", dateRange[0].startDate.toISOString());
        params.append("endDate", dateRange[0].endDate.toISOString());
      }

      // Add customer filter if set
      if (selectedCustomers.length > 0) {
        selectedCustomers.forEach((customer) => {
          params.append("customer_id", customer.value);
        });
      }

      // Add terminal filter if set
      if (selectedTerminals.length > 0) {
        selectedTerminals.forEach((terminal) => {
          params.append("terminal", terminal.value);
        });
      }

      // Add quality score range if modified
      if (qualityRange[0] > 0) {
        params.append("minQualityScore", qualityRange[0]);
      }

      if (qualityRange[1] < 100) {
        params.append("maxQualityScore", qualityRange[1]);
      }

      // Add search term if set
      if (searchTerm) {
        params.append("referencia", searchTerm);
      }

      // Add abandoned filter if not 'all'
      if (abandonedFilter === "abandoned") {
        params.append("showAbandoned", "true");
      } else if (abandonedFilter === "active") {
        params.append("showAbandoned", "false");
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/data-quality/analytics`,
        { params }
      );

      setDataQuality(response.data);

      // Extract filter options from data
      const customers = response.data.customerStats.map((customer) => ({
        value: customer.customerId,
        label: customer.customerName,
      }));
      setCustomerOptions(customers);

      const terminals = response.data.terminalStats.map((terminal) => ({
        value: terminal.terminal,
        label: terminal.terminal,
      }));
      setTerminalOptions(terminals);

      // Set pagination
      const totalItems = response.data.bookings.length;
      const totalPages = Math.ceil(totalItems / pagination.pageSize);
      setPagination({
        ...pagination,
        totalItems,
        totalPages,
      });
    } catch (error) {
      console.error("Error fetching data quality analytics:", error);
      setError("Failed to fetch data quality analytics. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch booking details
  const fetchBookingDetails = async (bookingId) => {
    try {
      const response = await axios.get(
        `${process.env.API_URL_PROD}/api/data-quality/booking/${bookingId}`
      );
      setSelectedBooking(response.data);
      setShowBookingDetails(true);
    } catch (error) {
      console.error("Error fetching booking details:", error);
      // Show error notification
    }
  };

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchDataQualityAnalytics();
  }, [
    dateRange,
    selectedCustomers,
    selectedTerminals,
    qualityRange,
    searchTerm,
    abandonedFilter,
  ]);

  // Handle filter changes
  const handleResetFilters = () => {
    setSelectedCustomers([]);
    setSelectedTerminals([]);
    setQualityRange([0, 100]);
    setSearchTerm("");
    setAbandonedFilter("all");
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  // Custom select styles for filters
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
      borderRadius: "0.375rem",
      minHeight: "38px",
      transition: "all 0.2s ease",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e1effe",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      fontWeight: 500,
      fontSize: "0.8125rem",
      padding: "0.125rem 0.25rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#60a5fa",
      ":hover": {
        backgroundColor: "#bfdbfe",
        color: "#1e3a8a",
      },
      borderRadius: "0 0.25rem 0.25rem 0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e2e8f0",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#e1effe"
        : "white",
      color: state.isSelected ? "white" : "#1e293b",
      fontSize: "0.875rem",
      padding: "0.5rem 0.75rem",
      ":active": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#bfdbfe",
      },
    }),
  };

  // Toggle chart layout
  const toggleChartLayout = () => {
    setChartLayout((prev) => ({
      ...prev,
      columns: prev.columns === 2 ? 3 : 2, // Toggle between 2 and 3 columns
    }));
  };

  // Toggle chart size
  const toggleChartSize = () => {
    const sizes = ["small", "medium", "large"];
    const currentIndex = sizes.indexOf(chartLayout.size);
    const nextIndex = (currentIndex + 1) % sizes.length;

    setChartLayout((prev) => ({
      ...prev,
      size: sizes[nextIndex],
    }));
  };

  // Determine chart size class based on layout settings
  const getChartSizeClass = () => {
    const columnClasses = {
      1: "grid-cols-1",
      2: "grid-cols-1 lg:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    };

    const sizeClasses = {
      small: "h-64",
      medium: "h-80",
      large: "h-96",
    };

    return {
      columns: columnClasses[chartLayout.columns],
      height: sizeClasses[chartLayout.size],
    };
  };

  // Check if any filters are active
  const hasActiveFilters =
    selectedCustomers.length > 0 ||
    selectedTerminals.length > 0 ||
    qualityRange[0] > 0 ||
    qualityRange[1] < 100 ||
    abandonedFilter !== "all" ||
    (dateRange[0].startDate && dateRange[0].endDate) ||
    searchTerm.length > 0;

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "N/A";

      const options = { year: "numeric", month: "short", day: "numeric" };
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  // Get quality score color class
  const getQualityScoreColorClass = (score) => {
    if (score >= 90) return "text-green-600";
    if (score >= 70) return "text-blue-600";
    if (score >= 50) return "text-yellow-600";
    if (score >= 30) return "text-orange-600";
    return "text-red-600";
  };

  // Get quality score background class
  const getQualityScoreBgClass = (score) => {
    if (score >= 90) return "bg-green-100";
    if (score >= 70) return "bg-blue-100";
    if (score >= 50) return "bg-yellow-100";
    if (score >= 30) return "bg-orange-100";
    return "bg-red-100";
  };

  // Calculate paginated data for bookings table
  const paginatedBookings = dataQuality?.bookings
    ? dataQuality.bookings.slice(
        (pagination.currentPage - 1) * pagination.pageSize,
        pagination.currentPage * pagination.pageSize
      )
    : [];

  const chartSizeClasses = getChartSizeClass();

  // Render loading spinner
  if (isLoading && !dataQuality) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-3xl font-semibold animate-spin text-blue-600 mr-3"
        />
        <div className="text-2xl font-semibold">
          Loading Data Quality Dashboard...
        </div>
      </div>
    );
  }

  // Render error message
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg max-w-lg text-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-3xl mb-3"
          />
          <div className="text-xl font-semibold mb-2">
            Error Loading Dashboard
          </div>
          <p>{error}</p>
          <button
            onClick={fetchDataQualityAnalytics}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* Header with title */}
      <div className="w-full bg-white shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-[#1A2654] to-blue-700 text-white">
          {/* Main header with title and action buttons */}
          <div className="flex justify-between items-center px-6 pt-5 pb-6">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faChartBar} className="text-2xl" />
              <h1 className="text-2xl font-bold tracking-tight">
                Data Quality Analytics Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => fetchDataQualityAnalytics()}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                disabled={isLoading}
              >
                <FontAwesomeIcon
                  icon={isLoading ? faSpinner : faSync}
                  className={isLoading ? "animate-spin" : ""}
                />
                <span>{isLoading ? "Refreshing..." : "Refresh Data"}</span>
              </button>
              <button
                onClick={toggleChartLayout}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faChartBar} />
                <span>{`${chartLayout.columns} Columns`}</span>
              </button>
              <button
                onClick={toggleChartSize}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faChartBar} />
                <span>{`${
                  chartLayout.size.charAt(0).toUpperCase() +
                  chartLayout.size.slice(1)
                } Size`}</span>
              </button>
              <button
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                onClick={() => {
                  if (dataQuality) {
                    // Logic to export data as CSV or Excel
                    // Implementation would depend on your export library
                    alert("Export functionality would be implemented here");
                  }
                }}
                disabled={!dataQuality}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Export Data</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-6">
        {/* Search and Filter Bar */}
        <div className="mb-6">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                {/* Search input */}
                <div className="w-full md:w-1/3">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="text-gray-400"
                      />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Search references, booking codes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Buttons group */}
                <div className="flex flex-wrap gap-2 justify-end ml-auto">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showFilters
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    <span>Filters</span>
                    <FontAwesomeIcon
                      icon={showFilters ? faChevronDown : faChevronDown}
                      className="text-xs"
                    />
                  </button>

                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showDatePicker ||
                      (dateRange[0].startDate && dateRange[0].endDate)
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Date Range</span>
                    {showDatePicker ? (
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="text-xs"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="text-xs"
                      />
                    )}
                  </button>

                  <button
                    onClick={handleResetFilters}
                    disabled={!hasActiveFilters}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      hasActiveFilters
                        ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Clear All Filters</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Date picker dropdown */}
            {showDatePicker && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium text-gray-700">
                    Select Date Range
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setDateRange([
                          {
                            startDate: null,
                            endDate: null,
                            key: "selection",
                          },
                        ]);
                      }}
                      className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                    >
                      Clear Range
                    </button>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="text-sm text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div className="flex justify-center">
                  <div className="border border-gray-300 rounded-lg shadow-md bg-white">
                    <DateRangePicker
                      ranges={dateRange}
                      onChange={(ranges) => setDateRange([ranges.selection])}
                      months={2}
                      direction="horizontal"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Filter Panel */}
            {showFilters && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faBuilding}
                        className="mr-2 text-blue-600"
                      />
                      Customer
                    </label>
                    <Select
                      options={customerOptions}
                      isMulti
                      onChange={setSelectedCustomers}
                      value={selectedCustomers}
                      placeholder="Select Customers"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className="mr-2 text-blue-600"
                      />
                      Terminal
                    </label>
                    <Select
                      options={terminalOptions}
                      isMulti
                      onChange={setSelectedTerminals}
                      value={selectedTerminals}
                      placeholder="Select Terminals"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="mr-2 text-blue-600"
                      />
                      Quality Score Range
                    </label>
                    <div className="px-2">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">
                          {qualityRange[0]}
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={qualityRange[0]}
                          onChange={(e) =>
                            setQualityRange([
                              parseInt(e.target.value),
                              qualityRange[1],
                            ])
                          }
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">
                          {qualityRange[1]}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-sm text-gray-600">
                          {qualityRange[0]}
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={qualityRange[1]}
                          onChange={(e) =>
                            setQualityRange([
                              qualityRange[0],
                              parseInt(e.target.value),
                            ])
                          }
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">
                          {qualityRange[1]}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="mr-2 text-blue-600"
                      />
                      Abandoned Filter
                    </label>
                    <div className="flex space-x-2">
                      <button
                        className={`px-3 py-2 text-sm font-medium rounded ${
                          abandonedFilter === "all"
                            ? "bg-blue-100 text-blue-700 border border-blue-300"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                        }`}
                        onClick={() => setAbandonedFilter("all")}
                      >
                        All Bookings
                      </button>
                      <button
                        className={`px-3 py-2 text-sm font-medium rounded ${
                          abandonedFilter === "abandoned"
                            ? "bg-red-100 text-red-700 border border-red-300"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                        }`}
                        onClick={() => setAbandonedFilter("abandoned")}
                      >
                        Abandoned Only
                      </button>
                      <button
                        className={`px-3 py-2 text-sm font-medium rounded ${
                          abandonedFilter === "active"
                            ? "bg-green-100 text-green-700 border border-green-300"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                        }`}
                        onClick={() => setAbandonedFilter("active")}
                      >
                        Active Only
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="mb-6 flex flex-wrap gap-2">
            {selectedCustomers.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Customer:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedCustomers((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedTerminals.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Terminal:</span> {item.label}
                <button
                  onClick={() => {
                    setSelectedTerminals((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {(qualityRange[0] > 0 || qualityRange[1] < 100) && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <span className="font-medium mr-1">Quality Score:</span>{" "}
                {qualityRange[0]} - {qualityRange[1]}%
                <button
                  onClick={() => setQualityRange([0, 100])}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}

            {abandonedFilter !== "all" && (
              <span
                className={`px-2 py-1 rounded-full flex items-center text-xs transition-all border ${
                  abandonedFilter === "abandoned"
                    ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                    : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                }`}
              >
                <span className="font-medium mr-1">Status:</span>
                {abandonedFilter === "abandoned"
                  ? "Abandoned Only"
                  : "Active Only"}
                <button
                  onClick={() => setAbandonedFilter("all")}
                  className={`ml-1 ${
                    abandonedFilter === "abandoned"
                      ? "text-red-500 hover:text-red-700"
                      : "text-green-500 hover:text-green-700"
                  } transition-colors`}
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}

            {searchTerm && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <span className="font-medium mr-1">Search:</span> {searchTerm}
                <button
                  onClick={() => setSearchTerm("")}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}

            {dateRange[0].startDate && dateRange[0].endDate && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                <span className="font-medium mr-1">Date Range:</span>
                {formatDate(dateRange[0].startDate)} -{" "}
                {formatDate(dateRange[0].endDate)}
                <button
                  onClick={() => {
                    setDateRange([
                      {
                        startDate: null,
                        endDate: null,
                        key: "selection",
                      },
                    ]);
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Overall Metrics */}
        {dataQuality && (
          <div className="mb-6">
            <OverallQualityMetrics metrics={dataQuality.metrics} />
          </div>
        )}

        {/* Charts */}
        {dataQuality && (
          <div className={`grid ${chartSizeClasses.columns} gap-6 mb-6`}>
            {/* Quality Distribution Chart */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <QualityDistributionChart
                distribution={dataQuality.metrics.qualityDistribution}
                totalBookings={dataQuality.metrics.totalBookings}
              />
            </div>

            {/* Quality Trend Chart */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <QualityTrendChart trendData={dataQuality.metrics.qualityTrend} />
            </div>

            {/* Status Progression Chart */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <StatusProgressionChart
                statusData={dataQuality.metrics.statusProgression}
                statusCompletion={dataQuality.metrics.statusCompletion}
                totalBookings={dataQuality.metrics.totalBookings}
                expectedStatusProgression={
                  dataQuality.expectedStatusProgression
                }
                statusDescriptions={dataQuality.statusDescriptions}
              />
            </div>

            {/* Date Quality Analysis */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <DateQualityAnalysisChart
                dateQuality={dataQuality.metrics.dateQuality}
                totalBookings={dataQuality.metrics.totalBookings}
              />
            </div>

            {/* Abandoned Bookings Analysis */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <AbandonedBookingsChart
                abandonedCount={dataQuality.metrics.abandonedCount}
                totalBookings={dataQuality.metrics.totalBookings}
                qualityTrend={dataQuality.metrics.qualityTrend}
              />
            </div>

            {/* Terminal Quality Comparison */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <TerminalQualityComparison
                terminalStats={dataQuality.terminalStats}
              />
            </div>

            {/* Customer Quality Comparison */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <CustomerQualityComparison
                customerStats={dataQuality.customerStats}
              />
            </div>

            {/* Aduana Quality Comparison */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <AduanaQualityComparison aduanaStats={dataQuality.aduanaStats} />
            </div>

            {/* Data Quality Issues Matrix */}
            <div
              className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${chartSizeClasses.height}`}
            >
              <DataQualityIssuesMatrix
                bookings={dataQuality.bookings}
                onViewDetails={(bookingId) => fetchBookingDetails(bookingId)}
              />
            </div>
          </div>
        )}

        {/* Bookings Table */}
        {dataQuality && (
          <div className="bg-white shadow-lg rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FontAwesomeIcon
                  icon={faIdCard}
                  className="text-blue-600 mr-2"
                />
                Bookings Data Quality
              </h3>

              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-500">
                  Showing {paginatedBookings.length} of{" "}
                  {dataQuality.bookings.length} bookings
                </span>

                <button className="flex items-center space-x-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors">
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            {/* Bookings Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Reference
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Booking Code
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Customer
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Terminal
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Created
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Quality Score
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status Quality
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date Quality
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedBookings.length === 0 ? (
                    <tr>
                      <td
                        colSpan="10"
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No bookings found matching your criteria
                      </td>
                    </tr>
                  ) : (
                    paginatedBookings.map((booking) => (
                      <tr key={booking._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium">
                            {booking.referencia}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {booking.bookingCode || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium">
                            {booking.cliente?.razonSocial ||
                              booking.cliente?.id ||
                              "N/A"}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {booking.cliente?.rfc || ""}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {booking.terminal || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(booking.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getQualityScoreBgClass(
                              booking.overallQualityScore
                            )} ${getQualityScoreColorClass(
                              booking.overallQualityScore
                            )}`}
                          >
                            {booking.overallQualityScore.toFixed(1)}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {booking.isAbandoned ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Abandoned
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              booking.statusQuality.isValid
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {booking.statusQuality.score.toFixed(1)}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              booking.dateQuality.isValid
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {booking.dateQuality.score.toFixed(1)}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => fetchBookingDetails(booking._id)}
                            className="text-blue-600 hover:text-blue-800 transition-colors font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <select
                  className="mr-2 border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={pagination.pageSize}
                  onChange={(e) =>
                    setPagination({
                      ...pagination,
                      pageSize: Number(e.target.value),
                      currentPage: 1, // Reset to first page when changing page size
                      totalPages: Math.ceil(
                        dataQuality.bookings.length / Number(e.target.value)
                      ),
                    })
                  }
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-500">
                  Showing{" "}
                  {dataQuality.bookings.length > 0
                    ? (pagination.currentPage - 1) * pagination.pageSize + 1
                    : 0}{" "}
                  to{" "}
                  {Math.min(
                    pagination.currentPage * pagination.pageSize,
                    dataQuality.bookings.length
                  )}{" "}
                  of {dataQuality.bookings.length} bookings
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setPagination({ ...pagination, currentPage: 1 })
                  }
                  disabled={
                    pagination.currentPage === 1 || pagination.totalPages === 0
                  }
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === 1 || pagination.totalPages === 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  First
                </button>

                <button
                  onClick={() =>
                    setPagination({
                      ...pagination,
                      currentPage: pagination.currentPage - 1,
                    })
                  }
                  disabled={
                    pagination.currentPage === 1 || pagination.totalPages === 0
                  }
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === 1 || pagination.totalPages === 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      // Create a window of pages centered around current page
                      let pageWindow = [];
                      if (pagination.totalPages <= 5) {
                        // Show all pages if 5 or fewer
                        pageWindow = Array.from(
                          { length: pagination.totalPages },
                          (_, i) => i + 1
                        );
                      } else {
                        // Create window around current page
                        const start = Math.max(
                          1,
                          Math.min(
                            pagination.currentPage - 2,
                            pagination.totalPages - 4
                          )
                        );
                        pageWindow = Array.from(
                          { length: 5 },
                          (_, i) => start + i
                        );
                      }

                      return pageWindow.map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() =>
                            setPagination({
                              ...pagination,
                              currentPage: pageNum,
                            })
                          }
                          className={`w-8 h-8 flex items-center justify-center rounded-md mx-0.5 ${
                            pagination.currentPage === pageNum
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {pageNum}
                        </button>
                      ));
                    }
                  )}
                </div>

                <button
                  onClick={() =>
                    setPagination({
                      ...pagination,
                      currentPage: pagination.currentPage + 1,
                    })
                  }
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === pagination.totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  Next
                </button>

                <button
                  onClick={() =>
                    setPagination({
                      ...pagination,
                      currentPage: pagination.totalPages,
                    })
                  }
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`px-2 py-1 text-sm rounded ${
                    pagination.currentPage === pagination.totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <BookingQualityDetailsModal
          bookingData={selectedBooking}
          isOpen={showBookingDetails}
          onClose={() => setShowBookingDetails(false)}
        />
      )}
    </div>
  );
};

export default DataQualityDashboard;
